import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';

/* ─── Design tokens ─────────────────────────────────────────────────── */
const C = {
  navy:        '#0e225d',
  navyMid:     '#1a3370',
  navyDark:    '#0a1a3d',
  gold:        '#fbb21f',
  goldLight:   '#ffc93c',
  goldDim:     'rgba(251,178,31,0.18)',
  goldBorder:  'rgba(251,178,31,0.35)',
  white:       '#ffffff',
  border:      '#e5e7eb',
  bg:          '#f5f7fa',
  muted:       '#9ca3af',
  sub:         '#6b7280',
  body:        '#1f2937',
  green:       '#16a34a',
  greenDim:    'rgba(22,163,74,0.12)',
  greenBorder: 'rgba(22,163,74,0.3)',
  red:         '#dc2626',
  redDim:      'rgba(220,38,38,0.08)',
  redBorder:   'rgba(220,38,38,0.25)',
};

/* ─── Types ─────────────────────────────────────────────────────────── */
interface ClassTest {
  class_test_id:   string;
  institute_id:    string;
  institute_name:  string | null;
  branch_id:       string | null;
  branch_name:     string | null;
  batch_id:        string | null;
  batch_name:      string | null;
  title:           string;
  subject:         string;
  schedule_date:   string | null;
  schedule_time:   string | null;
  status:          'scheduled' | 'conducted' | 'cancelled';
  conducted_date:  string | null;
  questions:       { marks: number }[];
  created_at:      string | null;
  created_by:      string | null;
  created_by_name: string | null;
}

interface AttemptResult {
  obtained:        number;
  total:           number;
  answered:        number;
  skipped:         number;
  total_questions: number;
  submitted_at:    string | null;
}

interface AttemptStatus {
  attempted:   boolean;
  test_status: string;
  can_attempt: boolean;
  result:      AttemptResult | null;
}

/* ─── Helpers ───────────────────────────────────────────────────────── */
const STATUS_MAP = {
  scheduled: { bg: C.goldDim,  text: '#a06000', border: C.goldBorder,  icon: 'pi-calendar-clock', label: 'Scheduled' },
  conducted: { bg: C.redDim,   text: C.red,     border: C.redBorder,   icon: 'pi-lock',           label: 'Closed'    },
  cancelled: { bg: C.redDim,   text: C.red,     border: C.redBorder,   icon: 'pi-times-circle',   label: 'Cancelled' },
};

function pct(obtained: number, total: number) {
  return total ? Math.round((obtained / total) * 100) : 0;
}
function scoreColor(p: number) {
  return p >= 75 ? C.green : p >= 50 ? '#d97706' : C.red;
}
function scoreLabel(p: number) {
  return p >= 90 ? 'Excellent' : p >= 75 ? 'Good' : p >= 50 ? 'Average' : 'Needs Work';
}

/* ─── Sub-components ────────────────────────────────────────────────── */
const InfoField: React.FC<{ label: string; value: string | null; valueColor?: string }> = ({ label, value, valueColor }) => (
  <div>
    <p style={{ margin: '0 0 5px 0', fontSize: '0.68rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px' }}>{label}</p>
    <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: valueColor || C.body }}>{value || '—'}</p>
  </div>
);

const SectionCard: React.FC<{ title: string; icon: string; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div style={{ background: C.white, borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
      <div style={{ width: '40px', height: '40px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className={`pi ${icon}`} style={{ color: C.gold, fontSize: '1.1rem' }} />
      </div>
      <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', color: C.navy }}>{title}</h2>
    </div>
    {children}
  </div>
);

/* ─── Score Ring ─────────────────────────────────────────────────────── */
const ScoreRing: React.FC<{ obtained: number; total: number }> = ({ obtained, total }) => {
  const p    = pct(obtained, total);
  const r    = 54;
  const circ = 2 * Math.PI * r;
  const dash = (p / 100) * circ;
  const col  = scoreColor(p);
  return (
    <div style={{ position: 'relative', width: '140px', height: '140px', flexShrink: 0 }}>
      <svg width="140" height="140" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="70" cy="70" r={r} fill="none" stroke={C.bg} strokeWidth="10" />
        <circle cx="70" cy="70" r={r} fill="none" stroke={col} strokeWidth="10"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 1s ease-out' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: '1.6rem', fontWeight: '800', color: col, lineHeight: 1 }}>{p}%</span>
        <span style={{ fontSize: '0.65rem', fontWeight: '700', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '3px' }}>{scoreLabel(p)}</span>
      </div>
    </div>
  );
};

/* ─── Progress Bar Row ───────────────────────────────────────────────── */
const ProgressRow: React.FC<{ label: string; value: number; total: number; color: string }> = ({ label, value, total, color }) => (
  <div>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
      <span style={{ fontSize: '0.78rem', fontWeight: '600', color: C.sub }}>{label}</span>
      <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.body }}>{value} / {total}</span>
    </div>
    <div style={{ height: '6px', background: C.bg, borderRadius: '6px', overflow: 'hidden', border: `1px solid ${C.border}` }}>
      <div style={{ height: '100%', width: `${total ? (value / total) * 100 : 0}%`, background: color, borderRadius: '6px', transition: 'width 0.8s ease-out' }} />
    </div>
  </div>
);

/* ─── Main Component ─────────────────────────────────────────────────── */
const ViewClassTest: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  const [test,    setTest]    = useState<ClassTest | null>(null);
  const [attempt, setAttempt] = useState<AttemptStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  useEffect(() => { if (id) fetchAll(); }, [id]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const [testRes, attemptRes] = await Promise.all([
        apiClient.get(`/v1/class-tests/${id}`),
        apiClient.get(`/v1/class-tests/${id}/attempt-status`),
      ]);
      if (testRes.data.success)    setTest(testRes.data.data);
      if (attemptRes.data.success) setAttempt(attemptRes.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load class test');
    } finally {
      setLoading(false);
    }
  };

  /* ── Loading ──────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: C.white, borderRadius: '16px', border: `1px solid ${C.border}` }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth="3" animationDuration=".8s" />
          <p style={{ marginTop: '16px', color: C.navy, fontSize: '0.95rem', fontWeight: '500' }}>Loading class test…</p>
        </div>
      </div>
    );
  }

  /* ── Error ────────────────────────────────────────────────────────── */
  if (error || !test) {
    return (
      <div style={{ padding: '24px' }}>
        <Message severity="error" text={error || 'Class test not found'} style={{ marginBottom: '20px', fontSize: '0.9rem' }} />
        <button
          onClick={() => navigate(-1)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: C.navy, border: 'none', borderRadius: '10px', color: C.white, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer' }}
        >
          <i className="pi pi-arrow-left" /> Go Back
        </button>
      </div>
    );
  }

  const status     = STATUS_MAP[test.status] ?? STATUS_MAP.scheduled;
  const totalMarks = test.questions.reduce((s, q) => s + q.marks, 0);
  const result     = attempt?.result ?? null;
  const p          = result ? pct(result.obtained, result.total) : 0;

  return (
    <>
      <style>{`
        .vct-start:hover { transform: translateY(-2px) !important; box-shadow: 0 8px 24px rgba(22,163,74,0.45) !important; }
        .vct-back:hover  { background: rgba(255,255,255,0.18) !important; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .vct-anim { animation: fadeIn 0.2s ease-out; }
      `}</style>

      <Toast ref={toast} />

      <div style={{ margin: '0 auto', minHeight: '100vh' }}>

        {/* ── Hero Header ─────────────────────────────────────────────── */}
        <div style={{
          background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 50%, ${C.navyDark} 100%)`,
          borderRadius: '20px', padding: '32px', marginBottom: '24px',
          color: C.white, position: 'relative', overflow: 'hidden',
          boxShadow: '0 4px 18px rgba(14,34,93,0.25)',
        }}>
          <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '220px', height: '220px', background: C.goldDim, borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: '-40px', left: '30%', width: '140px', height: '140px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <button
              className="vct-back"
              onClick={() => navigate(-1)}
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: C.white, padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '20px', transition: 'all 0.2s ease' }}
            >
              <i className="pi pi-arrow-left" /> Back
            </button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '24px' }}>

              {/* Left — title + chips */}
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '20px', marginBottom: '12px', fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: C.gold }}>
                  <i className="pi pi-book" style={{ fontSize: '0.7rem' }} />
                  Class Test
                </div>
                <h1 style={{ margin: '0 0 6px 0', fontSize: '1.75rem', fontWeight: '700', color: C.white, letterSpacing: '-0.5px' }}>
                  {test.title}
                </h1>
                <p style={{ margin: '0 0 16px 0', color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem' }}>
                  {test.branch_name ?? '—'}{test.batch_name ? ` · ${test.batch_name}` : ''}
                </p>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {[
                    { icon: 'pi-list',     label: `${test.questions.length} Questions` },
                    { icon: 'pi-star',     label: `${totalMarks} Marks`               },
                    ...(test.schedule_date ? [{ icon: 'pi-calendar', label: test.schedule_date }] : []),
                    ...(test.schedule_time ? [{ icon: 'pi-clock',    label: test.schedule_time }] : []),
                  ].map(chip => (
                    <div key={chip.label} style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', padding: '8px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <i className={`pi ${chip.icon}`} style={{ fontSize: '0.8rem', color: C.gold }} />
                      <span style={{ fontSize: '0.82rem', fontWeight: '600', color: C.white }}>{chip.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right — status + attempt badge */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.5px', background: status.bg, color: status.text, border: `1px solid ${status.border}` }}>
                  <i className={`pi ${status.icon}`} style={{ fontSize: '0.72rem' }} />
                  {status.label.toUpperCase()}
                </span>

                {attempt?.attempted ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700', background: C.greenDim, color: '#166534', border: `1px solid ${C.greenBorder}` }}>
                    <i className="pi pi-check-circle" style={{ fontSize: '0.72rem' }} />
                    SUBMITTED
                  </span>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700', background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.15)' }}>
                    <i className="pi pi-minus-circle" style={{ fontSize: '0.72rem' }} />
                    NOT ATTEMPTED
                  </span>
                )}

                {test.created_at && (
                  <p style={{ margin: 0, fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)' }}>
                    Created {test.created_at.split(' ')[0]}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Body grid ───────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px', marginBottom: '24px' }}>

          {/* ── Left column ───────────────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* ── Result card (attempted) ───────────────────────────── */}
            {attempt?.attempted && result && (
              <SectionCard title="Your Result" icon="pi-chart-line">
                <div style={{ display: 'flex', alignItems: 'center', gap: '28px', flexWrap: 'wrap' }}>
                  <ScoreRing obtained={result.obtained} total={result.total} />
                  <div style={{ flex: 1, minWidth: '180px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div style={{ padding: '16px', background: C.bg, borderRadius: '12px', border: `1px solid ${C.border}` }}>
                      <p style={{ margin: '0 0 4px', fontSize: '0.65rem', fontWeight: '700', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Score</p>
                      <p style={{ margin: 0, fontSize: '2rem', fontWeight: '800', color: scoreColor(p), lineHeight: 1 }}>
                        {result.obtained} <span style={{ fontSize: '1rem', color: C.muted, fontWeight: '600' }}>/ {result.total}</span>
                      </p>
                    </div>
                    <ProgressRow label="Answered" value={result.answered} total={result.total_questions} color={C.green} />
                    <ProgressRow label="Skipped"  value={result.skipped}  total={result.total_questions} color={C.red}   />
                  </div>
                </div>
                {result.submitted_at && (
                  <p style={{ margin: '18px 0 0', fontSize: '0.75rem', color: C.muted, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <i className="pi pi-clock" style={{ fontSize: '0.72rem' }} />
                    Submitted on {result.submitted_at.split(' ')[0]} at {result.submitted_at.split(' ')[1]}
                  </p>
                )}
              </SectionCard>
            )}

            {/* ── Not attempted CTA ─────────────────────────────────── */}
            {!attempt?.attempted && (
              <div style={{ background: C.white, borderRadius: '16px', padding: '32px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}`, textAlign: 'center' }}>
                <div style={{ width: '64px', height: '64px', background: attempt?.can_attempt ? C.goldDim : C.redDim, border: `1.5px solid ${attempt?.can_attempt ? C.goldBorder : C.redBorder}`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <i className={`pi ${attempt?.can_attempt ? 'pi-play-circle' : 'pi-lock'}`} style={{ fontSize: '1.6rem', color: attempt?.can_attempt ? C.gold : C.red }} />
                </div>
                {attempt?.can_attempt ? (
                  <>
                    <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: '700', color: C.navy }}>Ready to begin?</h3>
                    <p style={{ margin: '0 0 22px', fontSize: '0.88rem', color: C.sub, maxWidth: '340px', marginInline: 'auto' }}>
                      This test has {test.questions.length} questions worth {totalMarks} marks. Once you start, complete it in one go.
                    </p>
                    <button
                      className="vct-start"
                      onClick={() => navigate(`/exams/class-tests/${test.class_test_id}/take`)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '14px 32px', background: C.green, border: 'none', borderRadius: '12px', color: C.white, fontSize: '0.92rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 14px rgba(22,163,74,0.35)', transition: 'all 0.25s', letterSpacing: '0.3px' }}
                    >
                      <i className="pi pi-play-circle" style={{ fontSize: '1rem' }} />
                      Start Exam
                    </button>
                  </>
                ) : (
                  <>
                    <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: '700', color: C.navy }}>Test Not Available</h3>
                    <p style={{ margin: 0, fontSize: '0.88rem', color: C.sub }}>
                      {test.status === 'conducted' ? 'This test has already been conducted.' : 'This test has been cancelled.'}
                    </p>
                  </>
                )}
              </div>
            )}

            {/* ── Test Details ──────────────────────────────────────── */}
            <SectionCard title="Test Details" icon="pi-file-edit">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px' }}>
                <InfoField label="Subject"       value={test.subject} />
                <InfoField label="Batch"         value={test.batch_name} />
                <InfoField label="Branch"        value={test.branch_name} />
                <InfoField label="Schedule Date" value={test.schedule_date} />
                <InfoField label="Schedule Time" value={test.schedule_time} />
                <InfoField label="Status"        value={status.label} valueColor={status.text} />
                {test.conducted_date && (
                  <InfoField label="Conducted On" value={test.conducted_date.split(' ')[0]} />
                )}
              </div>
            </SectionCard>
          </div>

          {/* ── Right sidebar ─────────────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* At a Glance */}
            <div style={{ background: C.white, borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div style={{ width: '36px', height: '36px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="pi pi-chart-bar" style={{ color: C.gold, fontSize: '1rem' }} />
                </div>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: C.navy }}>At a Glance</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  { icon: 'pi-list', label: 'Total Questions', value: test.questions.length },
                  { icon: 'pi-star', label: 'Total Marks',     value: totalMarks            },
                  ...(attempt?.attempted && result ? [
                    { icon: 'pi-check-circle', label: 'Answered', value: result.answered },
                    { icon: 'pi-minus-circle', label: 'Skipped',  value: result.skipped  },
                  ] : []),
                ].map(stat => (
                  <div key={stat.label} style={{ display: 'flex', alignItems: 'center', gap: '12px', background: C.bg, padding: '12px', borderRadius: '10px', border: `1px solid ${C.border}` }}>
                    <div style={{ width: '38px', height: '38px', flexShrink: 0, background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className={`pi ${stat.icon}`} style={{ color: C.gold, fontSize: '0.9rem' }} />
                    </div>
                    <div>
                      <p style={{ margin: '0 0 2px 0', fontSize: '0.68rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{stat.label}</p>
                      <p style={{ margin: 0, fontSize: '1.4rem', fontWeight: '800', color: C.navy, lineHeight: 1.1 }}>{stat.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA or submitted state */}
            {attempt?.can_attempt ? (
              <button
                className="vct-start"
                onClick={() => navigate(`/exams/class-tests/${test.class_test_id}/take`)}
                style={{ width: '100%', padding: '14px', background: C.green, border: 'none', borderRadius: '12px', color: C.white, fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 14px rgba(22,163,74,0.3)', transition: 'all 0.25s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '9px' }}
              >
                <i className="pi pi-play-circle" style={{ fontSize: '0.95rem' }} />
                Start Exam
              </button>
            ) : attempt?.attempted ? (
              <div style={{ background: C.greenDim, border: `1px solid ${C.greenBorder}`, borderRadius: '12px', padding: '14px', textAlign: 'center' }}>
                <i className="pi pi-check-circle" style={{ color: C.green, fontSize: '1.3rem', display: 'block', marginBottom: '6px' }} />
                <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: '700', color: '#166534' }}>Test submitted</p>
                {result && (
                  <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: C.green }}>
                    {result.obtained} / {result.total} marks
                  </p>
                )}
              </div>
            ) : (
              <div style={{ background: C.redDim, border: `1px solid ${C.redBorder}`, borderRadius: '12px', padding: '14px', textAlign: 'center' }}>
                <i className="pi pi-lock" style={{ color: C.red, fontSize: '1.3rem', display: 'block', marginBottom: '6px' }} />
                <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: '700', color: C.red }}>Test unavailable</p>
              </div>
            )}

            {/* Tips — only before attempt */}
            {!attempt?.attempted && attempt?.can_attempt && (
              <div style={{ background: C.white, borderRadius: '14px', padding: '18px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                  <div style={{ width: '34px', height: '34px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="pi pi-lightbulb" style={{ color: C.gold, fontSize: '0.9rem' }} />
                  </div>
                  <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: C.navy }}>Before you start</h3>
                </div>
                <ul style={{ margin: 0, padding: '0 0 0 18px', display: 'flex', flexDirection: 'column', gap: '7px' }}>
                  {[
                    'You can only submit once.',
                    'Each question may carry different marks.',
                    'Unanswered questions count as skipped.',
                    'Stay on the page until you submit.',
                  ].map(tip => (
                    <li key={tip} style={{ fontSize: '0.78rem', color: C.sub, lineHeight: 1.5 }}>{tip}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Submitted on */}
            {attempt?.attempted && result?.submitted_at && (
              <div style={{ background: C.white, borderRadius: '14px', padding: '18px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
                <p style={{ margin: '0 0 6px', fontSize: '0.65rem', fontWeight: '700', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Submitted On</p>
                <p style={{ margin: '0 0 3px', fontSize: '0.9rem', fontWeight: '700', color: C.navy }}>{result.submitted_at.split(' ')[0]}</p>
                <p style={{ margin: 0, fontSize: '0.8rem', color: C.sub }}>{result.submitted_at.split(' ')[1]}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewClassTest;